import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import MainIcon from '../../../../img/svg/main'
import Currency from '../../../Elements/currency'
import { PickupMonitorCardCustomerName, PickupMonitorCardFooter, PickupMonitorCardHeader, PickupMonitorCardInfoWrap, PickupMonitorCardOrderNumberLabel, PickupMonitorCardOrderNumberText, PickupMonitorCardOrderNumberWrap, PickupMonitorCardOrderPlatformIcon, PickupMonitorCardPayment, PickupMonitorCardPaymentTotal, PickupMonitorCardWrap } from './index.styled'
import PickupMonitorActionButton from './buttons/actionButton'
import PlatformIcon from '../../../../img/svg/platforms'
import { useDispatch, useSelector } from 'react-redux'
import { selectCompanyData } from '../../../../redux/selector/system'
import { useTheme } from 'styled-components'
import PickupMonitorOptionButton from './buttons/optionButton'
import { updateOrderPickupStatusThunk } from '../../../../redux/thunk/pickupMonitor'
import { setPickupMonitorSelectedOrder, setPickupMonitorShowOptionsModal, setPickupMonitorShowOrderModal } from '../../../../redux/action/pickupMonitor'
import { trimRefId } from './helper'

const PickupMonitorOrderCard = React.memo(({ order, index }) => {
	const { t } = useTranslation()
	const countryCode = useSelector(selectCompanyData('countryCode'))
	const isOrdersStatusUpdating = useSelector(store => store.pickupMonitor.isOrdersStatusUpdating)
	const { colorTheme } = useTheme()
	const dispatch = useDispatch()
	const [orderStatusIsUpdating, setOrderStatusIsUpdating] = useState({ actionButton: false, optionButton: false })
	const isActionButtonOrderStatusUpdating = orderStatusIsUpdating.actionButton
	const isOptionButtonOrderStatusUpdating = orderStatusIsUpdating.optionButton

	const lieferandoIcon = countryCode === 'CHE' ? 'lieferando-che' : 'lieferando-de'
	const platformIcon = order?.platformMetaData?.platformType ?
		(order?.platformMetaData?.platformType === 'TAKEAWAY_PLUGIN' ? lieferandoIcon : order?.platformMetaData?.platformType) :
		(order?.kioskMetaData ? 'kiosk' : '')
	const orderPickupStatus = order?.pickupMetaData?.status
	const isAwaitingPickupOrder = orderPickupStatus === 'AWAITING_PICKUP'
	const isProcessingPickupOrder = orderPickupStatus === 'PROCESSING_PICKUP'
	const layout = (isAwaitingPickupOrder ? 'awaiting-pickup ' : '') + (isProcessingPickupOrder ? 'processing-pickup ' : '')
	const isUnpaidOrder = order.payments.some(payment => !payment.payed)

	const handleSelectOrder = useCallback(() => {
		dispatch(setPickupMonitorSelectedOrder(order))
		dispatch(setPickupMonitorShowOrderModal(true))
	}, [dispatch, order])

	const handleOrderPickupStatus = useCallback(async (updatableStatus = null, buttonType) => {
		if (isUnpaidOrder) {
			dispatch(setPickupMonitorSelectedOrder(order))
			dispatch(setPickupMonitorShowOptionsModal(true))
		} else if (updatableStatus) {
			setOrderStatusIsUpdating(prevState => ({ ...prevState, [buttonType]: true }))
			await dispatch(updateOrderPickupStatusThunk(order.orderId, updatableStatus))
			setOrderStatusIsUpdating({ actionButton: false, optionButton: false })
		}
	}, [isUnpaidOrder, order, dispatch])

	return (
		<PickupMonitorCardWrap className={layout} data-testid={`${orderPickupStatus}-card-${index}`}>
			<PickupMonitorCardHeader>
				<PickupMonitorCardOrderNumberWrap>
					{order?.orderDayCounter ?
						<PickupMonitorCardOrderNumberLabel className="order-day-counter">
							<PickupMonitorCardOrderNumberText>{order.orderDayCounter}</PickupMonitorCardOrderNumberText>
						</PickupMonitorCardOrderNumberLabel> :
						<PickupMonitorCardOrderNumberLabel />}
					{(order?.platformMetaData || order?.kioskMetaData) &&
						<PickupMonitorCardOrderNumberLabel className="platform">
							<PickupMonitorCardOrderNumberText className="platform">
								{order?.platformMetaData && trimRefId(order?.platformMetaData?.refId)}
								{order?.kioskMetaData && trimRefId(order?.kioskMetaData?.refId)}
							</PickupMonitorCardOrderNumberText>
							{order.platformMetaData?.platformType === 'API_PLUGIN' ?
								<PickupMonitorCardOrderNumberText className="platform">{order.platformMetaData?.platformName}</PickupMonitorCardOrderNumberText> :
								<PickupMonitorCardOrderPlatformIcon>
									<PlatformIcon icon={`${platformIcon}-${colorTheme}`} />
								</PickupMonitorCardOrderPlatformIcon>}
						</PickupMonitorCardOrderNumberLabel>}
				</PickupMonitorCardOrderNumberWrap>

				<PickupMonitorCardInfoWrap>
					{order.customer?.name && <PickupMonitorCardCustomerName>{order.customer?.name}</PickupMonitorCardCustomerName>}
					{order.payments.map((payment, i) =>
						<PickupMonitorCardPayment key={i}>
							<MainIcon icon={payment?.paymentName ? 'other-payment' : payment.method} />
							<PickupMonitorCardPaymentTotal><Currency>{payment.total}</Currency></PickupMonitorCardPaymentTotal>
						</PickupMonitorCardPayment>)}
				</PickupMonitorCardInfoWrap>
			</PickupMonitorCardHeader>
			<PickupMonitorActionButton isUnpaidOrder={isUnpaidOrder}
																 status={orderPickupStatus}
																 onClick={handleOrderPickupStatus}
																 disabled={isOrdersStatusUpdating || isActionButtonOrderStatusUpdating || isOptionButtonOrderStatusUpdating}
																 isOrderStatusUpdating={isActionButtonOrderStatusUpdating}
																 buttonType="actionButton" />

			<PickupMonitorCardFooter>
				{isAwaitingPickupOrder && <PickupMonitorOptionButton icon="lf-grid"
																														 text={t('PickupMonitor.buttons.other')}
																														 className="small"
																														 onClick={handleSelectOrder}
																														 disabled={isOrdersStatusUpdating} />}
				{isProcessingPickupOrder &&
					<>
						<PickupMonitorOptionButton icon="arrow-right-thin"
																			 text={t('PickupMonitor.buttons.returnToAwaiting')}
																			 className="awaiting-pickup"
																			 onClick={() => handleOrderPickupStatus('AWAITING_PICKUP', 'optionButton')}
																			 disabled={isOrdersStatusUpdating || isActionButtonOrderStatusUpdating || isOptionButtonOrderStatusUpdating}
																			 isOrderStatusUpdating={isOptionButtonOrderStatusUpdating} />
						<PickupMonitorOptionButton icon="lf-grid"
																			 text={t('PickupMonitor.buttons.other')}
																			 onClick={handleSelectOrder}
																			 disabled={isOrdersStatusUpdating} />
					</>
				}
			</PickupMonitorCardFooter>
		</PickupMonitorCardWrap>
	)
})

PickupMonitorOrderCard.displayName = 'PickupMonitorOrderCard'
export default PickupMonitorOrderCard
